import React, { useCallback, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { ActionType, ActionValue } from '../../type';
import { Button, CommonModal, HelperTooltip, Tips } from '@/components';
import { useSwitch } from '@/hooks';
import { clsx } from 'clsx';
import { IconAction, IconAdd, IconClose } from '@/assets/quest';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { QuestActionsConfig, QuestActionList } from '../../const';
import { useActionLoading } from './state';
import { formatNetworth } from '@/utils';
import { PRECISION } from '../PayInfoCard/hooks';
import { isArray, omit } from 'lodash';

export type ActionValueField = {
  type?: ActionType;
  data?: ActionValue;
};

type ActionSelectType = {
  value?: ActionValueField;
  onChange: (val: ActionValueField) => void;
  actionUnitFee?: number;
};

const TabList = QuestActionsConfig.map(i => ({
  label: i.groupLabel,
  value: i.group,
}));

export const ActionSelect = ({ value, onChange, actionUnitFee }: ActionSelectType) => {
  const { on, turnOff, turnOn } = useSwitch();
  const { selectedLabel, selectedIcon, customIconUrl } = useMemo(() => {
    if (value?.type && value?.data) {
      const item = QuestActionList.find(
        i => i.type === value.type || i.type?.includes(value.type!),
      );
      return {
        selectedLabel: item?.renderLabel?.(value.data),
        selectedIcon: item?.icon,
        customIconUrl: item?.customIcon ? value?.data?.[item?.customIcon] : '',
      };
    }
    return {};
  }, [value?.data, value?.type]);

  const handleSubmit = useCallback(val => onChange(val), [onChange]);

  return (
    <>
      <div className={styles.actionSelect} onClick={turnOn}>
        <IconAction className={styles.icon} />
        <div className={styles.actionLabel}>
          {selectedLabel ? (
            <div className={styles.selected}>
              {customIconUrl ? (
                <img src={customIconUrl} alt="icon" className={styles.actionIcon} />
              ) : (
                React.createElement(selectedIcon, {
                  className: styles.actionIcon,
                })
              )}
              {selectedLabel}
            </div>
          ) : (
            'Set up action'
          )}
        </div>
        <div className={styles.actionValue}>
          {actionUnitFee ? formatNetworth(actionUnitFee / PRECISION) : '$-'}
          <Tips title="This action incurs a small fee to guarantee the action is verified." />
        </div>
      </div>
      {on ? (
        <ActionSelectModal initValue={value} turnOff={turnOff} onSubmit={handleSubmit} />
      ) : null}
    </>
  );
};

const ActionSelectModal = ({
  turnOff,
  onSubmit,
  initValue,
}: {
  turnOff: () => void;
  onSubmit: (val: ActionValueField) => void;
  initValue?: ActionValueField;
}) => {
  const method = useForm<ActionValueField>({
    mode: 'onChange',
    defaultValues: {
      type: initValue?.type,
      data: initValue?.data || {},
    },
  });
  const [isActionLoading, setActionLoading] = useActionLoading();

  const { watch, handleSubmit, clearErrors, reset, formState } = method;

  const { errors, isValid } = formState;

  const defaultTab =
    QuestActionsConfig.find(i =>
      i?.actions.find(
        i => i.type === initValue?.type || i.type?.includes(initValue?.type as ActionType),
      ),
    )?.group || TabList[0].value;

  const [tab, setTab] = useState(defaultTab);
  const selectType = watch('type');

  const displayActions = useMemo(
    () => QuestActionsConfig.find(i => i.group === tab)?.actions,
    [tab],
  );

  const currentAction = useMemo(
    () => QuestActionList?.find(i => i.type === selectType || i.type?.includes(selectType!)),
    [selectType],
  );

  const displayEditor = useMemo(
    () =>
      QuestActionList?.find(i => i.type === selectType || i.type?.includes(selectType!))
        ?.renderEditor,
    [selectType],
  );

  const isRequireError = Object.entries(errors?.data || {})?.some(
    // @ts-ignore
    ([_, val]) => val?.type === 'required',
  );

  const handleClickItem = useCallback(
    type => {
      if (isActionLoading) {
        return;
      }
      if (type === selectType) {
        return;
      }
      let newType;
      const isInitType = type === initValue?.type || type?.includes(initValue?.type);
      if (isInitType) {
        newType = initValue?.type;
      } else {
        newType = isArray(type) ? type?.[0] : type;
      }
      reset({ type: newType, data: isInitType ? initValue?.data : {} });
      clearErrors();
    },
    [clearErrors, isActionLoading, initValue?.data, initValue?.type, reset, selectType],
  );

  const handleClickTab = useCallback(
    (value: string) => {
      if (isActionLoading) {
        return;
      }
      setTab(value);
    },
    [isActionLoading],
  );

  const handleClickCloseAction = useCallback(() => {
    reset({ type: undefined, data: {} });
  }, [reset]);

  const handleClose = useCallback(() => {
    setActionLoading(false);
    turnOff();
  }, [setActionLoading, turnOff]);

  const submit: SubmitHandler<ActionValueField> = useCallback(
    data => {
      // 内部临时使用，不提交_headers
      onSubmit(omit(data, 'data._headers'));
      handleClose();
    },
    [handleClose, onSubmit],
  );

  return (
    <CommonModal turnOff={handleClose} containerClassName={styles.modalContainer} hideCloseBtn>
      <div className={styles.title}>Set up action</div>
      <div className={styles.content}>
        <div className={styles.actions}>
          <div className={styles.tab}>
            {TabList.map(({ label, value }) => (
              <div
                className={clsx(styles.tabItem, value === tab && styles.selected)}
                onClick={() => handleClickTab(value)}
                key={value}>
                {label}
              </div>
            ))}
          </div>
          <div className={styles.actionList}>
            {displayActions?.map(({ title, icon: IconComponent, type, desc }) => (
              <HelperTooltip title={desc}>
                <div
                  className={clsx(
                    styles.actionItem,
                    (selectType === type || type?.includes(selectType!)) && styles.selected,
                  )}
                  onClick={() => handleClickItem(type)}>
                  <IconComponent className={styles.icon} key={type} />
                  {title}
                  <IconAdd className={styles.addIcon} />
                </div>
              </HelperTooltip>
            ))}
          </div>
        </div>
        <div className={styles.editor}>
          <div className={styles.editorInner}>
            <div className={styles.editorTitle}>
              Edit Credential info
              {isRequireError ? (
                <div className={styles.requiredMessage}>Please fill all fields correctly</div>
              ) : null}
            </div>
            {displayEditor ? (
              <div>
                <div className={styles.actionHeader}>
                  <div className={styles.actionName}>
                    {currentAction?.icon
                      ? React.createElement(currentAction?.icon, {
                          className: styles.actionIcon,
                        })
                      : null}
                    {currentAction?.title}
                  </div>
                  <IconClose className={styles.iconClose} onClick={handleClickCloseAction} />
                </div>
                <div className={styles.editorWrap}>
                  <FormProvider {...method}>{displayEditor()}</FormProvider>
                </div>
              </div>
            ) : (
              <div className={styles.emptyEditor}>
                The selected credential will be displayed here.
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.btnContainer}>
        <Button className={clsx(styles.btn, styles.cancelBtn)} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          className={clsx(styles.btn, styles.saveBtn)}
          disabled={!isValid || isActionLoading || isActionLoading}
          onClick={handleSubmit(submit)}>
          Save
        </Button>
      </div>
    </CommonModal>
  );
};
